/**
 * Work Queue List
 * Virtualized scrollable list with loading/error/empty states
 */

import { useEffect, useMemo, useRef, useCallback, useState } from 'react'
import { Spinner, Button, makeStyles, tokens } from '@fluentui/react-components'
import { useVirtualizer } from '@tanstack/react-virtual'
import type { IssueSummary } from '../../../domain'
import { useExperienceMode } from '../../../context/ExperienceModeContext'
import { WorkQueueItem } from './WorkQueueItem'

const ESTIMATED_ITEM_HEIGHT = 72

const useStyles = makeStyles({
  scrollContainer: {
    height: '100%',
    overflowY: 'auto',
  },
  virtualList: {
    position: 'relative',
    width: '100%',
  },
  virtualItem: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
  },
  centered: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: tokens.spacingVerticalXL,
    gap: tokens.spacingVerticalM,
  },
  message: {
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    textAlign: 'center',
  },
  demoButton: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2,
    borderTopColor: tokens.colorPaletteYellowBorder1,
    borderRightColor: tokens.colorPaletteYellowBorder1,
    borderBottomColor: tokens.colorPaletteYellowBorder1,
    borderLeftColor: tokens.colorPaletteYellowBorder1,
  },
})

function EmptyState() {
  const styles = useStyles()
  const { experienceMode, enterDemoMode } = useExperienceMode()

  return (
    <div className={styles.centered}>
      <span className={styles.message}>No issues in queue</span>
      {experienceMode !== 'demo' && (
        <Button appearance="secondary" className={styles.demoButton} onClick={enterDemoMode}>
          Switch to demo mode
        </Button>
      )}
    </div>
  )
}

export interface WorkQueueListProps {
  issues: IssueSummary[]
  loading: boolean
  error: Error | null
  selectedIssueId: string | null
  onSelectIssue: (id: string) => void
  onRetry?: () => void
  onSelectionAnnounce?: (announcement: string) => void
}

export function WorkQueueList({
  issues,
  loading,
  error,
  selectedIssueId,
  onSelectIssue,
  onRetry,
  onSelectionAnnounce,
}: WorkQueueListProps) {
  const styles = useStyles()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Map<number, HTMLElement>>(new Map())

  // Find the selected index for keyboard navigation
  const selectedIndex = useMemo(() => {
    if (!selectedIssueId) return -1
    return issues.findIndex((issue) => issue.id === selectedIssueId)
  }, [issues, selectedIssueId])

  // Track focused index for roving tabindex (using state to trigger re-renders for tabIndex/aria-activedescendant)
  const [focusedIndex, setFocusedIndex] = useState(selectedIndex >= 0 ? selectedIndex : 0)

  // Sync focused index when selection changes
  useEffect(() => {
    if (selectedIndex >= 0) {
      setFocusedIndex(selectedIndex)
    }
  }, [selectedIndex])

  // Handle case where focused item is beyond list bounds
  useEffect(() => {
    if (issues.length === 0) {
      setFocusedIndex(0)
    } else if (focusedIndex >= issues.length) {
      setFocusedIndex(issues.length - 1)
    }
  }, [issues.length, focusedIndex])

  // Setup virtualizer
  const virtualizer = useVirtualizer({
    count: issues.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ESTIMATED_ITEM_HEIGHT,
    overscan: 5,
  })

  // Stable callback for item selection
  const handleSelectIssue = useCallback(
    (id: string) => {
      onSelectIssue(id)
    },
    [onSelectIssue]
  )

  // Get tab index for roving tabindex pattern
  const getTabIndex = useCallback(
    (index: number): 0 | -1 => {
      if (issues.length === 0) return index === 0 ? 0 : -1
      return index === focusedIndex ? 0 : -1
    },
    [issues.length, focusedIndex]
  )

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (issues.length === 0) return

      let newIndex = focusedIndex

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault()
          newIndex = Math.max(0, focusedIndex - 1)
          break
        case 'ArrowDown':
          event.preventDefault()
          newIndex = Math.min(issues.length - 1, focusedIndex + 1)
          break
        case 'Home':
          event.preventDefault()
          newIndex = 0
          break
        case 'End':
          event.preventDefault()
          newIndex = issues.length - 1
          break
        case 'Enter':
        case ' ':
          event.preventDefault()
          if (focusedIndex >= 0 && focusedIndex < issues.length) {
            onSelectIssue(issues[focusedIndex].id)
          }
          return
        default:
          return
      }

      if (newIndex !== focusedIndex) {
        setFocusedIndex(newIndex)

        // Scroll the item into view using virtualizer
        virtualizer.scrollToIndex(newIndex, { align: 'auto' })

        // Focus the element after scrolling (with delay to ensure render)
        requestAnimationFrame(() => {
          const element = itemRefs.current.get(newIndex)
          if (element) {
            element.focus()
          }
        })
      }
    },
    [issues, onSelectIssue, virtualizer, focusedIndex]
  )

  // Set ref for virtual item
  const setItemRef = useCallback((index: number, el: HTMLDivElement | null) => {
    if (el) {
      itemRefs.current.set(index, el)
    } else {
      itemRefs.current.delete(index)
    }
  }, [])

  // Announce when selection changes
  useEffect(() => {
    if (selectedIssueId && onSelectionAnnounce) {
      const issue = issues.find((i) => i.id === selectedIssueId)
      if (issue) {
        onSelectionAnnounce(`Selected: ${issue.title}`)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- issues excluded intentionally
  }, [selectedIssueId, onSelectionAnnounce])

  // Get the focused item's id for aria-activedescendant
  const focusedItemId = issues[focusedIndex]
    ? `work-queue-item-${issues[focusedIndex].id}`
    : undefined

  if (loading) {
    return (
      <div className={styles.centered}>
        <Spinner size="medium" label="Loading issues..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.centered} role="alert">
        <span className={styles.errorMessage}>Failed to load issues</span>
        {onRetry && (
          <Button appearance="secondary" onClick={onRetry}>
            Retry
          </Button>
        )}
      </div>
    )
  }

  if (issues.length === 0) {
    return (
      <EmptyState />
    )
  }

  const virtualItems = virtualizer.getVirtualItems()

  return (
    <div
      ref={scrollContainerRef}
      className={styles.scrollContainer}
      role="listbox"
      aria-label="Work queue issues"
      aria-activedescendant={focusedItemId}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div
        className={styles.virtualList}
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualItems.map((virtualItem) => {
          const issue = issues[virtualItem.index]
          return (
            <div
              key={virtualItem.key}
              className={styles.virtualItem}
              style={{
                transform: `translateY(${virtualItem.start}px)`,
              }}
              data-index={virtualItem.index}
            >
              <WorkQueueItem
                ref={(el) => setItemRef(virtualItem.index, el)}
                issue={issue}
                isSelected={issue.id === selectedIssueId}
                tabIndex={getTabIndex(virtualItem.index)}
                onSelect={handleSelectIssue}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
